import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import {
  ChevronDownIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';

import {
  loadPreferences,
  savePreferences,
} from '../../../redux/actions/preferences';
import { useAlert } from '../../../context/AlertContext';
import useLanguage from '../../../hooks/useLanguage';

/* ───────────────────────────── option tables ─────────────────────────────── */

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
];

// Stored as ISO codes; display labels are user-facing.
const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'GBP', label: 'GBP — British Pound' },
];

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin' },
  { value: 'America/New_York', label: 'America/New_York' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '24h', label: '24h' },
  { value: '12h', label: '12h' },
];

const DEFAULTS = {
  language: 'en',
  currency: 'EUR',
  timezone: 'Europe/Lisbon',
  dateFormat: 'YYYY-MM-DD',
  timeFormat: '24h',
  // Marketing consent is opt-in by default; settings rows created before this
  // key existed have no value, so `undefined` must read as opted in too.
  emailMarketingOptIn: true,
};

const inOptions = (options, value) =>
  options.some((o) => o.value === value);

/* ───────────────────────────── shared atoms ──────────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>}
    </div>
    {action}
  </header>
);

const SelectField = ({ label, value, options, onChange }) => (
  <label className="block">
    <span className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold">
      {label}
    </span>
    <div className="relative mt-1.5">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3.5 pr-9 rounded-xl bg-white border border-line text-[13.5px] text-ink-strong appearance-none focus:outline-none focus:border-yellow-03 transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
    </div>
  </label>
);

const Toggle = ({ checked, onChange, label, sub, isLast }) => (
  <label
    className={`flex items-start justify-between gap-6 py-4 cursor-pointer ${
      isLast ? '' : 'border-b border-line/70'
    }`}
  >
    <div className="min-w-0">
      <div className="text-[14.5px] text-ink-strong font-medium">{label}</div>
      {sub && (
        <div className="text-[13px] text-muted mt-0.5 leading-relaxed max-w-md">
          {sub}
        </div>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition-colors mt-0.5 ${
        checked ? 'bg-ink-strong' : 'bg-line'
      }`}
    >
      <span
        className={`absolute top-0.5 ${
          checked ? 'left-[22px]' : 'left-0.5'
        } w-5 h-5 rounded-full bg-white transition-all shadow`}
      />
    </button>
  </label>
);

/* ────────────────────────────── Preferences ──────────────────────────────── */

const Preferences = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { preferences, loading } = useSelector((s) => s.preferences || {});
  const { language, setLanguage } = useLanguage();

  const isBrand = useSelector((s) => s.auth?.user?.role) === 'brand';

  // Local form state — language is applied immediately via setLanguage; the
  // others stay local until the user clicks Save (matches legacy behaviour).
  const [currency, setCurrency] = useState(DEFAULTS.currency);
  const [timezone, setTimezone] = useState(DEFAULTS.timezone);
  const [dateFormat, setDateFormat] = useState(DEFAULTS.dateFormat);
  const [timeFormat, setTimeFormat] = useState(DEFAULTS.timeFormat);
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(
    DEFAULTS.emailMarketingOptIn
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(loadPreferences());
  }, [dispatch]);

  // Hydrate local state from backend prefs
  useEffect(() => {
    if (!preferences) return;

    if (
      preferences.language &&
      LANGUAGE_OPTIONS.some((o) => o.value === preferences.language) &&
      preferences.language !== i18n.language
    ) {
      setLanguage(preferences.language);
    }
    if (
      preferences.currency &&
      inOptions(CURRENCY_OPTIONS, preferences.currency)
    ) {
      setCurrency(preferences.currency);
    }
    if (
      preferences.timezone &&
      inOptions(TIMEZONE_OPTIONS, preferences.timezone)
    ) {
      setTimezone(preferences.timezone);
    }
    if (
      preferences.dateFormat &&
      inOptions(DATE_FORMAT_OPTIONS, preferences.dateFormat)
    ) {
      setDateFormat(preferences.dateFormat);
    }
    if (
      preferences.timeFormat &&
      inOptions(TIME_FORMAT_OPTIONS, preferences.timeFormat)
    ) {
      setTimeFormat(preferences.timeFormat);
    }
    if (typeof preferences.emailMarketingOptIn === 'boolean') {
      setEmailMarketingOptIn(preferences.emailMarketingOptIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  const initialValues = useMemo(
    () => ({
      language: preferences?.language || DEFAULTS.language,
      currency: preferences?.currency || DEFAULTS.currency,
      timezone: preferences?.timezone || DEFAULTS.timezone,
      dateFormat: preferences?.dateFormat || DEFAULTS.dateFormat,
      timeFormat: preferences?.timeFormat || DEFAULTS.timeFormat,
      emailMarketingOptIn:
        typeof preferences?.emailMarketingOptIn === 'boolean'
          ? preferences.emailMarketingOptIn
          : DEFAULTS.emailMarketingOptIn,
    }),
    [preferences]
  );

  const isDirty =
    language !== initialValues.language ||
    currency !== initialValues.currency ||
    timezone !== initialValues.timezone ||
    dateFormat !== initialValues.dateFormat ||
    timeFormat !== initialValues.timeFormat ||
    emailMarketingOptIn !== initialValues.emailMarketingOptIn;

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        language,
        timezone,
        currency,
        dateFormat,
        timeFormat,
        emailMarketingOptIn,
      };
      // Keep an audit trail of when consent was given or withdrawn.
      if (emailMarketingOptIn !== initialValues.emailMarketingOptIn) {
        payload.emailMarketingUpdatedAt = new Date().toISOString();
      }
      await dispatch(savePreferences(payload));
      localStorage.setItem('user-language-preference', language);
      localStorage.removeItem('user-language-session');
      showAlert({
        title: t('dashboard.preferences_page.saved_title'),
        message: t('dashboard.preferences_page.saved_message'),
        primaryAction: { label: t('common.ok') },
      });
    } catch (err) {
      showAlert({
        title: t('dashboard.preferences_page.save_error_title'),
        message: t('dashboard.preferences_page.save_error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setSaving(false);
    }
  }, [
    dispatch,
    language,
    timezone,
    currency,
    dateFormat,
    timeFormat,
    emailMarketingOptIn,
    initialValues.emailMarketingOptIn,
    showAlert,
    t,
  ]);

  const handleReset = useCallback(() => {
    setLanguage(DEFAULTS.language);
    setCurrency(DEFAULTS.currency);
    setTimezone(DEFAULTS.timezone);
    setDateFormat(DEFAULTS.dateFormat);
    setTimeFormat(DEFAULTS.timeFormat);
    setEmailMarketingOptIn(DEFAULTS.emailMarketingOptIn);
    localStorage.setItem('user-language-preference', DEFAULTS.language);
    localStorage.removeItem('user-language-session');
  }, [setLanguage]);

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={t('dashboard.preferences_page.eyebrow')}
        title={t('dashboard.preferences_page.title')}
        sub={t('dashboard.preferences_page.subtitle')}
      />

      {loading && !preferences ? (
        <div className="flex items-center justify-center py-16">
          <ClipLoader color="#FFBD59" size={32} />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader
              eyebrow={t('dashboard.preferences_page.regional_eyebrow')}
              title={t('dashboard.preferences_page.regional_title')}
              sub={t('dashboard.preferences_page.regional_sub')}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <SelectField
                label={t('dashboard.language')}
                value={language}
                options={LANGUAGE_OPTIONS}
                onChange={setLanguage}
              />
              <SelectField
                label={t('dashboard.currency')}
                value={currency}
                options={CURRENCY_OPTIONS}
                onChange={setCurrency}
              />
              <SelectField
                label={t('dashboard.timezone')}
                value={timezone}
                options={TIMEZONE_OPTIONS}
                onChange={setTimezone}
              />
              <SelectField
                label={t('dashboard.date_format')}
                value={dateFormat}
                options={DATE_FORMAT_OPTIONS}
                onChange={setDateFormat}
              />
              <SelectField
                label={t('dashboard.time_format')}
                value={timeFormat}
                options={TIME_FORMAT_OPTIONS}
                onChange={setTimeFormat}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              eyebrow={t('dashboard.preferences_page.email_marketing_eyebrow')}
              title={t('dashboard.preferences_page.email_marketing_title')}
              sub={
                isBrand
                  ? t('dashboard.preferences_page.email_marketing_sub_brand')
                  : t('dashboard.preferences_page.email_marketing_sub_promoter')
              }
            />
            <Toggle
              label={t(
                'dashboard.preferences_page.email_marketing_consent_label'
              )}
              sub={t('dashboard.preferences_page.email_marketing_consent_sub')}
              checked={emailMarketingOptIn}
              onChange={setEmailMarketingOptIn}
              isLast
            />
          </Card>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" />
              {t('common.reset')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !isDirty}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <ClipLoader color="#1A1A1F" size={14} />
              ) : (
                <CheckIcon className="w-4 h-4" />
              )}
              {saving ? t('dashboard.saving') : t('common.save')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Preferences;
